import buildQuery from 'odata-query';
import { encodeURIOdata } from './usefulFunctions';
import endpoints from './commonVariables/endpoints';
import { defaultFilterTypes } from '../types/lists/listTypes';
import {
    convertDateToEndOfTimeZone,
    convertDateToTimeZone,
    convertDateToUTC,
    formatDateToEndOfDayUTC,
} from './formatDates';

export const resultsPerPage = 20;

/**
 * addCommonParameters. Función auxiliar para construir el endPoint.
 * Concatena el prefijo '/DataService/HotelOwnerEntityService.svc/' y el sufijo '&$inlinecount=allpages&__noLoadWidth=1'
 * antes y después de la query en cuestión.
 * @param {string} entityName
 * @param {*string} query
 */

const addCommonParameters = (entityName, query) => {
    const prefix = endpoints.odataService;
    const suffix = '&$inlinecount=allpages&__noLoadWidth=1';
    return prefix + entityName + query + suffix;
};

/**
 * getTypeaheadEndPoint
 * @param {* string} value valor escrito en el input del typeahead. Se usa para filtrar las opciones del typeahead
 * @param {* object} parameters objeto con propiedades para construir el endpoint
 * enabledKey Clave para filtrar las opciones habilitadas, suele ser Enabled o Active, algunas tablas no tienen esta propiedad
 * entityName nombre de la entidad de odata Country, OwnerType...
 * labelKey clave de la entidad que se va a usar para mostrar en las opciones del typeahead. Suele ser Caption, LocalizedCaption, Name
 */

export const getTypeaheadEndPoint = (value, parameters) => {
    const { entityName, labelKey, enabledKey } = parameters;
    const defaultFilterArray = [`substringof(%27${value}%27,${labelKey})`];
    // Si hay enabledKey (Enabled, Active) le añade un elemento más al array filter
    const filterArray = enabledKey
        ? defaultFilterArray.concat({
              [enabledKey]: true,
          })
        : defaultFilterArray;

    const queryParameters = {
        filter: {
            and: filterArray,
        },
    };
    const endPoint = addCommonParameters(
        entityName,
        buildQuery(queryParameters)
    );
    return endPoint;
};

export const getSelectedOptionEndPoint = (value, parameters) => {
    const { entityName, enabledKey } = parameters;

    const defaultFilterArray = [`Id eq '${value}'`];

    const filterArray = enabledKey
        ? defaultFilterArray.concat({
              [enabledKey]: true,
          })
        : defaultFilterArray;

    const queryParameters = {
        filter: {
            and: filterArray,
        },
    };
    const endPoint = addCommonParameters(
        entityName,
        buildQuery(queryParameters)
    );
    return endPoint;
};

export const buildOdataEndPoint = obj => {
    const prefix = endpoints.odataService;
    let { expand = '' } = obj;
    expand = encodeURIComponent(expand);
    const { entity, ...restProps } = obj;

    return `${prefix}${entity}${buildQuery({
        ...restProps,
        expand,
    })}&__noLoadWidth=1&$inlinecount=allpages`;
};

export const buildEndPoint = (parameters, page = 1) => {
    const { entityName, query } = parameters;
    const { select, expand, orderBy, top, filter } = query;

    const skip = top * (page - 1);
    const queryapi = addCommonParameters(
        entityName,
        buildQuery({
            select,
            expand: expand && encodeURIComponent(expand),
            orderBy,
            top,
            skip,
            filter,
        })
    );
    return queryapi;
};

export const substringof = (key, value) => {
    const encodedValue = encodeURIOdata(value.trim());
    return `substringof('${encodedValue}', ${key})`;
};

export const eq = (key, value) => ({ [key]: value });

export const stringEq = (key, value) => eq(key, `${value}`);

export const isBoolean = value => typeof value === 'boolean';

const createOdataDateFilter = (filterKey, rangeType, date) =>
    `${filterKey} ${rangeType} datetime'${date}'`;

const formatDateFilter = rangeType => (filterKey, value) => {
    return createOdataDateFilter(filterKey, rangeType, value);
};

const formatDateFrom = (filterKey, value) =>
    formatDateFilter('ge')(filterKey, convertDateToUTC(value));

const formatDateTo = (filterKey, value) =>
    formatDateFilter('le')(filterKey, formatDateToEndOfDayUTC(value));

const filterTypes = {
    [defaultFilterTypes.DEFAULT_STRING_FILTER_TYPE]: {
        // filtrado usual del string filter
        // Si en el estado está como value: hilton => (substringof('hilton', filterKey))
        groupCondition: filter => {
            return (
                filter.filterType ===
                    defaultFilterTypes.DEFAULT_STRING_FILTER_TYPE &&
                filter.value.trim()
            );
        },
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey } = filter;
                return substringof(filterKey, value);
            }),
    },
    [defaultFilterTypes.DEFAULT_ID_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType === defaultFilterTypes.DEFAULT_ID_FILTER_TYPE &&
            filter.value,
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey } = filter;
                return stringEq(filterKey, value);
            }),
    },
    [defaultFilterTypes.DEFAULT_BOOLEAN_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_BOOLEAN_FILTER_TYPE &&
            isBoolean(filter.value),
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey } = filter;
                return eq(filterKey, value);
            }),
    },
    [defaultFilterTypes.DEFAULT_STRING_ID_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_STRING_ID_FILTER_TYPE &&
            filter.value?.Id,
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey } = filter;
                return stringEq(filterKey, value.Id);
            }),
    },
    [defaultFilterTypes.DEFAULT_DATE_FROM_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_DATE_FROM_FILTER_TYPE &&
            filter.value,
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey } = filter;
                return formatDateFrom(filterKey, value);
            }),
    },
    [defaultFilterTypes.DEFAULT_DATE_TO_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_DATE_TO_FILTER_TYPE && filter.value,
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey } = filter;
                return formatDateTo(filterKey, value);
            }),
    },
    [defaultFilterTypes.DEFAULT_DATE_TIME_FROM_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_DATE_TIME_FROM_FILTER_TYPE &&
            filter.value,
        transform: filters =>
            filters.map(filter => {
                const { value, filterKey, timeZone } = filter;
                return createOdataDateFilter(
                    filterKey,
                    'ge',
                    convertDateToTimeZone(value, timeZone)
                );
            }),
    },
    [defaultFilterTypes.DEFAULT_DATE_TIME_TO_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_DATE_TIME_TO_FILTER_TYPE &&
            filter.value,
        transform: filters =>
            filters.map(filter => {
                // convertDateToEndOfTimeZone
                const { value, filterKey, timeZone } = filter;
                return createOdataDateFilter(
                    filterKey,
                    'le',
                    convertDateToEndOfTimeZone(value, timeZone)
                );
            }),
    },
    [defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE]: {
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE &&
            filter.value?.Id,

        transform: filters => {
            return filters.map(filter => {
                const { value, filterKey } = filter;
                return eq(filterKey, parseInt(value.Id));
            });
        },
    },
    [defaultFilterTypes.DEFAULT_INTEGER_SUB_ENTITY_ID_FILTER_TYPE]: {
        // example => value = {id: 1, IdSupplierHotel: 9998877} => IdSupplierHotel
        groupCondition: filter =>
            filter.filterType ===
                defaultFilterTypes.DEFAULT_INTEGER_SUB_ENTITY_ID_FILTER_TYPE &&
            filter.value?.[filter.filterKey],
        transform: filters => {
            return filters.map(filter => {
                const { value, filterKey } = filter;
                return eq(filterKey, parseInt(value[filterKey]));
            });
        },
    },
};

export const mapFiltersToOdata = filters => {
    const transformedFilters = [];
    const specificFilterList = Object.values(filters);
    for (const filterTypeKey in filterTypes) {
        const filterType = filterTypes[filterTypeKey];
        const { groupCondition, transform } = filterType;

        const filterGroup = specificFilterList.filter(groupCondition);
        const transformedGroup = transform(filterGroup);
        transformedFilters.push(transformedGroup);
    }
    return [{ and: transformedFilters }];
};
